import { DOCUMENT, isPlatformServer } from '@angular/common';
import {
  Injectable,
  PLATFORM_ID,
  RendererFactory2,
  inject,
} from '@angular/core';
import { Subject, debounceTime } from 'rxjs';
import {
  LOKALISE_ENV_ENABLED,
  LOKALISE_PROJECT_ID,
  LOKALISE_USER_ENABLED,
} from './provider';
import { TranslateService } from '@ngx-translate/core';

const lokaliseBodyClass = 'lokalise-coloring';
const LiveJsPanelElementId = 'lokalise-live-js-panel';
const openClass = 'keepopen';
const openDefaultText = 'select translation to edit';

@Injectable()
export class LokaliseService {
  public translationAdded = new Subject<void>();

  private _document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  private translateService = inject(TranslateService);
  private renderer = inject(RendererFactory2).createRenderer(null, null);

  // Injection tokens
  private enabledEnvToken = inject(LOKALISE_ENV_ENABLED);
  private enabledUserToken = inject(LOKALISE_USER_ENABLED);
  private projectIdToken = inject(LOKALISE_PROJECT_ID);

  public init(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    if (!this.enabledEnvToken) {
      return;
    }

    if (!this.enabledUserToken) {
      this.listenToEnableShortcut();
      return;
    }

    this.injectScript();
    this.injectCSS();
    this.listenToLanguageChange();
    this.listenToTranslation();
    this.listenToCDKOverlay();
    this.listenToLokaliseInitialized();
  }

  /**
   * Enable LiveJS
   * By default it's disabled, pressing `ctrl + e` will enable it.
   */
  private listenToEnableShortcut(): void {
    this.renderer.listen('document', 'keydown.control.e', (event: Event) => {
      event.preventDefault();
      localStorage.setItem('LOKALISE_ENABLED', '{}');
      location.reload();
      return false;
    });
  }

  /**
   * Whenever we translate something `translationAdded` will emit.
   * Emitted from LokaliseTranslatePipe and LokaliseTranslateService
   */
  private listenToTranslation(): void {
    this.translationAdded.pipe(debounceTime(50)).subscribe(() => {
      this.emitUpdateElements();
      setTimeout(() => this.emitUpdateElements(), 2000);
    });
  }

  /**
   * Listening to CDK overlay changes
   * Due to timing issues CDK listener could not be used
   */
  private listenToCDKOverlay(): void {
    const targetNode = document.querySelector('.cdk-overlay-container');

    const config = { attributes: true, childList: true, subtree: true };

    const observer = new MutationObserver(() => this.emitUpdateElements());

    observer.observe(targetNode, config);
  }

  /**
   * When we change language we need to notify LiveJS about it through custom event
   */
  private listenToLanguageChange(): void {
    this.translateService.onLangChange.subscribe((event) => {
      document.dispatchEvent(
        new CustomEvent('lokalise-update-locale', { detail: event.lang })
      );
    });
  }

  /**
   * Listen to when LiveJS is initialized
   */
  private listenToLokaliseInitialized(): void {
    const injectToLiveJSButtonsCallback = () => {
      this.injectButtons();
    };

    document.addEventListener(
      'lokalise-initialized',
      injectToLiveJSButtonsCallback,
      { once: true }
    );
  }

  /**
   * Inject script to load LiveJS. Once loaded it will emit 'lokalise-initialized'
   */
  private injectScript(): void {
    const script = document.createElement('script');
    script.type = `application/javascript`;
    script.text = `
    window.LOKALISE_CONFIG = {
      projectId: "${this.projectIdToken}",
      locale: "${this.translateService.currentLang}",
      plainKey: true,
    };
    (function () {
      var a = document.createElement("script");a.type = "text/javascript";a.async = !0;
      a.src = ["https://app.lokalise.com/live-js/script.min.js?", (new Date).getTime()].join("");
      document.body.appendChild(a)
    })();
    `;
    this._document.body.appendChild(script);
  }

  /**
   * Inject CSS to body to fix styling
   * This will add coloring for translatable elements
   */
  private injectCSS(): void {
    const style = document.createElement('style');
    style.textContent = `
    .lokalise-coloring [data-lokalise]:not(button) { background-color: #ffc4006b; }
    .lokalise-coloring button[data-lokalise] { outline: 3px solid #ffc4006b !important;}
    .lokalise-coloring .lokalise-live-js-edit-icon { display: inline-block !important; top: unset !important; }
    #content, app-side-menu {padding-bottom: 210px !important;}
    #${LiveJsPanelElementId}.${openClass} {height: 210px !important;}
    #${LiveJsPanelElementId}.${openClass}::after {
      content: "${openDefaultText}";
      font-size: 20px;
      justify-content: center;
      display: flex;
      padding-top: 50px;
      font-style: italic;
    }
    `;
    this._document.body.appendChild(style);
    document.body.classList.add(lokaliseBodyClass);
  }

  /**
   * Injecting buttons into LiveJS toolbar.
   * This will add:
   *  - Toggle click - if click through propagation is stopped on parent (REMOVED)
   *  - Toggle color - will show/hide background color of translatable text
   *  - Remove LiveJS - will disable this script and reload website
   *  - Toggle extended - forces the LiveJS panel to be extended even if no translation is selected
   */
  private injectButtons(): void {
    const container = document.querySelector(
      '#lokalise-live-js-panel .lokalise-live-js-container'
    );

    const createButton = (text: string, id: string, callback: () => void) => {
      const button = document.createElement('div');
      button.innerHTML = `
      <div class="lokalise-live-js-profile-container lokalise-live-js-float-right">
        <button class="lokalise-live-js-button" id="lokalise-${id}">${text}</button>
      </div>`;

      container.appendChild(button);

      document
        .getElementById(`lokalise-${id}`)
        .addEventListener('click', () => callback());
    };

    createButton(
      'Close',
      'remove-script',
      this.removeLiveJS.bind(this)
    );
    createButton(
      'Toggle highlight',
      'toggle-coloring',
      this.toggleColors.bind(this)
    );
    createButton(
      'Toggle extended',
      'toggle-extend',
      this.toggleExtended.bind(this)
    );
  }

  private toggleColors(): void {
    if (document.body.classList.contains(lokaliseBodyClass)) {
      document.body.classList.remove(lokaliseBodyClass);
    } else {
      document.body.classList.add(lokaliseBodyClass);
    }
  }

  private toggleExtended(): void {
    if (document.getElementById(LiveJsPanelElementId).classList.contains(openClass)) {
      document.getElementById(LiveJsPanelElementId).classList.remove(openClass);
    } else {
      document.getElementById(LiveJsPanelElementId).classList.add(openClass);
    }
  }

  private emitUpdateElements(): void {
    document.dispatchEvent(new Event('lokalise-update-elements'));
  }

  private removeLiveJS(): void {
    localStorage.removeItem('LOKALISE_ENABLED');
    location.reload();
  }
}
